import React, { useState, useCallback, useEffect } from "react";
import PrivateLayout from "../../layouts/PrivateLayout";
import useDecodedToken from "../../utils/useDecodedToken";
import { useSelectedCharacter } from "../../context/SelectedCharacterContext";
import { useNavigate } from "react-router-dom";
import {
  registerSoloDuoLadder,
  cancelSoloDuoLadderRegistration,
  fetchUserCharacters,
} from "../../api/api";

function SoloDuoManageRegistration() {
  const backgroundImage = "/ww-wallpaper.webp";
  const { token } = useDecodedToken();
  const { selectedCharacter, setSelectedCharacter } = useSelectedCharacter();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isCharactersModalOpen, setIsCharactersModalOpen] = useState(false);
  const [characters, setCharacters] = useState([]);
  const [isLoadingCharacters, setIsLoadingCharacters] = useState(false);

  const openCharactersModal = useCallback(() => {
    if (!token) {
      setFeedbackMessage("Please make sure you are logged in.");
      return;
    }

    setIsCharactersModalOpen(true);
  }, [token]);

  const closeCharactersModal = useCallback(() => {
    setIsCharactersModalOpen(false);
  }, []);

  useEffect(() => {
    if (!isCharactersModalOpen || !token) {
      return;
    }

    const loadCharacters = async () => {
      setIsLoadingCharacters(true);

      try {
        const data = await fetchUserCharacters(token);
        setCharacters(data || []);
      } catch (error) {
        console.error("Error fetching characters for Solo/Duo registration:", error);
        setFeedbackMessage(
          error.message || "Failed to load characters. Please try again."
        );
      } finally {
        setIsLoadingCharacters(false);
      }
    };

    loadCharacters();
  }, [isCharactersModalOpen, token]);

  const handleRegisterWithCharacter = useCallback(
    async (character) => {
      if (!token || !character) {
        console.error("Missing token or character for registration");
        setFeedbackMessage(
          "Please make sure you are logged in and have a character selected."
        );
        return;
      }

      try {
        setIsSubmitting(true);
        setFeedbackMessage("");

        const ladderCharacterId = character.characterId || character.id;
        const characterIdString = String(ladderCharacterId);

        console.log(
          "Attempting Solo/Duo ladder registration with character:",
          {
            id: character.id,
            characterId: character.characterId,
            ladderCharacterId,
            characterIdString,
            name: character.name,
            realm: character.realm?.name,
          }
        );

        const response = await registerSoloDuoLadder(token, {
          type: "solo",
          characterId: characterIdString,
          partnerCharacterId: null,
        });

        console.log("Solo/Duo ladder registration response:", response);
        setSelectedCharacter(character);
        setIsCharactersModalOpen(false);
        navigate("/solo-duo-ranking/standings");
      } catch (error) {
        console.error("Error registering for Solo/Duo ladder:", error);
        setFeedbackMessage(
          error.message || "Failed to register for the ladder."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [token, setSelectedCharacter, navigate]
  );

  const handleCancelRegistration = useCallback(async () => {
    if (!token) {
      console.error("Missing token for cancelling registration");
      setFeedbackMessage("Please make sure you are logged in.");
      return;
    }

    try {
      setIsSubmitting(true);
      setFeedbackMessage("");

      const response = await cancelSoloDuoLadderRegistration(token);

      console.log("Solo/Duo ladder cancel registration response:", response);
      setFeedbackMessage("Registration cancelled successfully.");
    } catch (error) {
      console.error("Error cancelling Solo/Duo ladder registration:", error);
      setFeedbackMessage(
        error.message || "Failed to cancel ladder registration."
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [token]);

  const isRegisterDisabled = isSubmitting || !token;
  const isCancelDisabled = isSubmitting || !token;

  return (
    <PrivateLayout backgroundImage={backgroundImage}>
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] text-center text-white bg-black/40 px-4">
        <h1 className="text-3xl md:text-5xl font-bold mb-4 drop-shadow-lg">
          Manage Solo/Duo Registration
        </h1>
        <p className="text-lg md:text-xl max-w-2xl drop-shadow mb-6">
          Use the options below to register for the Solo ladder or cancel your
          current registration.
        </p>

        <div className="flex flex-wrap justify-center gap-3 mb-6">
          <button
            type="button"
            onClick={openCharactersModal}
            disabled={isRegisterDisabled}
            className={`px-5 py-2 rounded-full text-sm md:text-base font-semibold shadow-md transition-colors ${
              isRegisterDisabled
                ? "bg-gray-700 text-gray-300 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-400"
            }`}
          >
            {isSubmitting ? "Processing..." : "Register for ladder"}
          </button>

          <button
            type="button"
            onClick={handleCancelRegistration}
            disabled={isCancelDisabled}
            className={`px-5 py-2 rounded-full text-sm md:text-base font-semibold shadow-md transition-colors ${
              isCancelDisabled
                ? "bg-gray-700 text-gray-300 cursor-not-allowed"
                : "bg-red-500 hover:bg-red-400"
            }`}
          >
            {isSubmitting ? "Processing..." : "Cancel registration"}
          </button>
        </div>

        {feedbackMessage && (
          <p className="text-sm text-gray-200 max-w-xl">{feedbackMessage}</p>
        )}

        {isCharactersModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md md:max-w-lg p-6 text-gray-900">
              <h2 className="text-2xl font-bold mb-4 text-center">
                Choose character for Solo ladder
              </h2>

              {isLoadingCharacters ? (
                <div className="flex items-center justify-center py-6">
                  <span className="text-gray-700">Loading characters...</span>
                </div>
              ) : characters.length === 0 ? (
                <p className="text-center text-gray-700 py-4">
                  No characters found. Please add characters first.
                </p>
              ) : (
                <ul className="max-h-80 overflow-y-auto divide-y divide-gray-200 mb-4">
                  {characters.map((character) => (
                    <li
                      key={character.id}
                      className="py-3 flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-3">
                        {character.mediaLinks && character.mediaLinks[0] && (
                          <img
                            src={character.mediaLinks[0].value}
                            alt={character.name}
                            className="w-8 h-8 rounded-full"
                          />
                        )}
                        <div className="flex flex-col items-start">
                          <span className="font-semibold">
                            {character.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {character.realm?.name}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRegisterWithCharacter(character)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs md:text-sm rounded-full hover:bg-blue-500 transition-colors"
                      >
                        Use this character
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeCharactersModal}
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-gray-200 text-gray-800 hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PrivateLayout>
  );
}

export default SoloDuoManageRegistration;


